import React, { useState, useEffect, useId } from "react";
import ReactDOM from "react-dom";
import { useHttpClient } from "shared/hooks/http-hook";
import { trackSuiteEvent } from "shared/util/track";
import "./LeadCaptureModal.css";

const CALENDLY_URL = "https://calendly.com/routebot/intro";

/**
 * Book a Demo — form-first lead yakalama modalı (frontend/app tarafı).
 *
 * Landing'deki BookDemo akışının uygulama içi karşılığı: e-posta + şirket
 * (+ opsiyonel isim/telefon) alır, /lead-emails'e source:"book_demo" olarak kaydeder,
 * sonra teşekkür ekranı + opsiyonel Calendly gösterir. Calendly'ye doğrudan atmaz.
 *
 * Telefon ASLA zorunlu değildir. trackSuiteEvent zaten yalnızca uluslararası (routebot)
 * temada çalışır; servistesin'i etkilemez.
 */
const BookDemoModal = ({ show, onClose, trigger = "app", onSuccess }) => {
  const { sendRequest } = useHttpClient();
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const titleId = useId();

  // Her açılışta forma dön: kişi tekrar "Book a demo"ya basınca eski "thank you"
  // ekranı değil, (gerekirse yanlış e-postayı düzeltebileceği) form görünür.
  useEffect(() => {
    if (show) {
      setSubmitted(false);
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, onClose]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !company.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/lead-emails`,
        "POST",
        JSON.stringify({
          email: email.trim(),
          company: company.trim(),
          name: name.trim() || undefined,
          phone: phone.trim() || undefined,
          source: "book_demo",
          pageUrl: window.location.href,
        }),
        { "Content-Type": "application/json" },
      );
      setSubmitted(true);
      trackSuiteEvent("Book a Demo — Submitted", "button_click", { trigger });
      if (onSuccess) onSuccess();
    } catch (err) {
      if (err.code === 429) {
        setError("Too many attempts. Please try again in a little while.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const content = (
    <div className="lead-capture-overlay" onClick={handleOverlayClick} role="presentation">
      <div className="lead-capture-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="lead-capture-header">
          <button type="button" className="lead-capture-close" onClick={onClose} aria-label="Close dialog">
            ×
          </button>
          <h3 id={titleId}>Book a demo</h3>
          {!submitted && (
            <div className="lead-capture-subtitle">
              <p>Tell us a little about you and we&apos;ll set up a personalized walkthrough with your data.</p>
            </div>
          )}
        </div>

        <div className="lead-capture-body">
          {submitted ? (
            <div className="lead-capture-success">
              <div className="lead-capture-success-icon" aria-hidden="true" />
              <h4>Thank you!</h4>
              <p>We&apos;ll reach out within a few hours.</p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="lead-capture-book-link"
                style={{ marginTop: "14px" }}
                onClick={() => trackSuiteEvent("Book a Demo — Pick a Time (Calendly)", "button_click", { trigger })}
              >
                Prefer to talk sooner? Pick a time →
              </a>
            </div>
          ) : (
            <>
              <form className="lead-capture-form" onSubmit={handleSubmit} noValidate>
                <label htmlFor="bd-email" className="lead-capture-label">
                  Work email
                </label>
                <input
                  id="bd-email"
                  type="email"
                  className="lead-capture-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  required
                />

                <label htmlFor="bd-company" className="lead-capture-label" style={{ marginTop: "16px" }}>
                  Company / Organization
                </label>
                <input
                  id="bd-company"
                  type="text"
                  className="lead-capture-input"
                  placeholder="e.g. Springfield School District"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoComplete="organization"
                  required
                />

                <label htmlFor="bd-name" className="lead-capture-label lead-capture-label--optional">
                  Name <span>(optional)</span>
                </label>
                <input
                  id="bd-name"
                  type="text"
                  className="lead-capture-input"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />

                <label htmlFor="bd-phone" className="lead-capture-label lead-capture-label--optional">
                  Phone <span>(optional)</span>
                </label>
                <input
                  id="bd-phone"
                  type="tel"
                  className="lead-capture-input"
                  placeholder="We'll only call if you want us to"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                />

                <button
                  type="submit"
                  className="lead-capture-submit lead-capture-submit--full"
                  disabled={submitting || !email.trim() || !company.trim()}
                >
                  {submitting ? "Sending…" : "Request my demo"}
                </button>
              </form>
              {error && (
                <div className="lead-capture-error" role="alert">
                  {error}
                </div>
              )}
              <div className="lead-capture-divider">or</div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="lead-capture-book-link"
                onClick={() => trackSuiteEvent("Book a Demo — Pick a Time (Calendly)", "button_click", { trigger })}
              >
                Pick a time directly →
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const portalRoot = document.getElementById("modal-hook");
  return portalRoot ? ReactDOM.createPortal(content, portalRoot) : content;
};

export default BookDemoModal;
